import React from "react";
import { FormControl, FormLabel, TextField } from "@mui/material";
import {
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
} from "react-hook-form";
import Feature from "src/common_constants/Feature";
import { useAppSelector } from "src/redux/hooks";
import { selectHasFeature } from "src/redux/slices/featuresSlice";
import {
  formatPhoneNumber,
  isPhoneFieldName,
  normalizeLegacyPhoneInput,
} from "src/utils";

interface InputFieldRegProps {
  fieldName: string;
  label?: string;
  fullWidth?: boolean;
  control: any;
  error?: boolean;
  helperText?:
    | string
    | null
    | undefined
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>;
  defaultValue?: string | null;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  other?: any;
  multiline?: boolean | undefined;
  isDisable?: boolean;
  isTextGrey?: boolean;
  style?: object;
  labelColor?: string;
  required?: boolean;
  yupSchema?: any;
  inputLabelProps?: any;
  isPhoneNumber?: boolean;
}

const InputFieldReg: React.FC<InputFieldRegProps> = ({
  fieldName,
  control,
  label,
  error,
  helperText,
  fullWidth,
  defaultValue,
  onBlur,
  onChange,
  other,
  multiline,
  isDisable = false,
  isTextGrey = true,
  style = {},
  required = false,
  labelColor,
  yupSchema,
  inputLabelProps,
  isPhoneNumber = false,
}) => {
  const formContext = useFormContext();
  const { getFieldState, formState } = formContext || {};
  const hasPhoneFormat = useAppSelector((state) =>
    selectHasFeature(state, Feature.PHONE_FORMAT),
  );
  const isPhoneField = isPhoneNumber || isPhoneFieldName(fieldName);

  // compute required flag:
  // priority: explicit required prop -> yupSchema (if provided) -> fallback to getFieldState (best-effort)
  const computeIsRequired = React.useCallback(() => {
    if (required) return true;

    try {
      if (yupSchema) {
        // prefer using describe() when available
        if (typeof yupSchema.describe === "function") {
          const desc = yupSchema.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests) {
            return f.tests.some((t: any) => t?.name === "required");
          }
        }
        const fieldSchema =
          yupSchema.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema.tests ?? fieldSchema._tests ?? [];
          if (Array.isArray(tests)) {
            return tests.some(
              (t: any) => t?.name === "required" || t?.TEST === "required"
            );
          }
        }
      }
    } catch {
      // ignore
    }

    try {
      const fieldState = getFieldState ? getFieldState(fieldName) : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, getFieldState]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() =>
    computeIsRequired()
  );
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  // Determine whether to show validation error: only after touch or submit
  const fieldState = getFieldState ? getFieldState(fieldName) : null;
  const isTouched = !!(
    (formState && formState.touchedFields && formState.touchedFields[fieldName]) ||
    fieldState?.isTouched
  );

  const showError = !!error && (isTouched || (formState && formState.isSubmitted));

  // Normalize helper text: show only when showing error
  const displayHelperText = showError
    ? typeof helperText === "string"
      ? helperText
      : helperText?.message?.toString() ?? ""
    : "";

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: showError ? "0px" : "2px",
      borderColor: showError ? "transparent" : "primary.main",
      borderRadius: "8px",
      color: "secondary.main",
      overflow: "hidden",
    },
    height: multiline ? "auto" : 38,
    backgroundColor: "rgba(63, 141, 124, 0.00)",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: showError ? "transparent" : "primary.main",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.85)",
    },
    ...style,
  };

  const getLabelWithAsterisk = () => {
    if (!label) return "";
    return isRequiredState ? `${label} *` : label;
  };

  

  return (
    <FormControl sx={{ width: "100%" }}>
      {labelColor && (
        <FormLabel
          sx={{ mt: 1, color: labelColor ? labelColor : "secondary.main" }}
          htmlFor={`${label}`}
        >
          {getLabelWithAsterisk()}
        </FormLabel>
      )}
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue != undefined ? defaultValue : ""}
        render={({ field }) => (
          <TextField
            {...field}
            value={
              isPhoneField && hasPhoneFormat
                ? formatPhoneNumber(field.value)
                : field.value
            }
            multiline={multiline}
            size="small"
            variant={isTextGrey ? "outlined" : "standard"}
            label={labelColor ? "" : getLabelWithAsterisk()}
            fullWidth={fullWidth || true}
            error={showError}
            helperText={displayHelperText}
            onBlur={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              field.onBlur();
              onBlur && onBlur(event);
            }}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              const value = isPhoneField
                ? hasPhoneFormat
                  ? formatPhoneNumber(event.target.value)
                  : normalizeLegacyPhoneInput(event.target.value)
                : event.target.value;
              event.target.value = value;
              field.onChange(value);
              onChange && onChange(event);
            }}
            disabled={isDisable}
            InputLabelProps={{
              sx: {
                color: "secondary.main",
                padding: "0 5px",
              },
              ...inputLabelProps,
            }}
            {...other}
            InputProps={{
              sx: commonTextFieldStyles,
              ...other?.InputProps,
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default InputFieldReg;
